import { db, timestamp } from "../../firebase/config.js";
import { v4 as uuid } from "uuid";
import { client } from "../../hooks/Client.js";
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import { Auth } from "../../StateManagment/Auth.js";
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import { useContext, useEffect, useState } from "react";
import UserNameMeta from "../user/UserNameMeta.jsx";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import AddItemTableRow from "../common/AddItemTableRow.jsx";
import Modal from "../common/Modal.jsx";
import getNoteTarget from "./getNoteTarget.js";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import DeleteModal from "../common/DeleteModal.jsx";
import softDeleteDoc from "../../helpers/softDeleteDoc.js";
import NoteBodyCell from "../notes/NoteBodyCell.jsx";

// AI-CONTEXT: See docs/features/task-notes-unified-source.md
// Notes written here are routed by task shape:
//   task.AdressID → AdressNotes  (shown on household Notities tab)
//   task.PersonaID → PersonaReports (shown on persona Rapport tab)
//   neither → TaskNotes (task-only)
const TaskNotes = ({task}) => {
    // Context
    const [auth] = useContext(Auth);
    const [, setSaved] = useContext(SavedIcon);

    // State
    const [showAddModal, setShowAddModal] = useState(false);
    const [titleInput, setTitleInput] = useState('');
    const [noteInput, setNoteInput] = useState('');
    const [editingDocid, setEditingDocid] = useState(null);
    const [dragArray, setDragArray] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteDocid, setDeleteDocid] = useState('');
    const [deleteName, setDeleteName] = useState('');

    // Hooks
    const target = getNoteTarget(task);

    // Firestore — read from the matching collection, filtered by TaskID
    const notes = useFirestoreGeneralOrderBy(target.collection, 'TaskID', task?.ID || '', 'Position', 'asc');

    useEffect(() => {
        setDragArray(Array.isArray(notes) ? notes : []);
    }, [notes]);

    const openCreateModal = () => {
        setEditingDocid(null);
        setTitleInput('');
        setNoteInput('');
        setShowAddModal(true);
    };

    const openEditModal = (note) => {
        setEditingDocid(note.docid);
        setTitleInput(note.Title || '');
        setNoteInput(note.Body || '');
        setShowAddModal(true);
    };

    const closeModal = (open) => {
        if (open) {
            setShowAddModal(true);
            return;
        }
        setShowAddModal(false);
        setEditingDocid(null);
        setTitleInput('');
        setNoteInput('');
    };

    const saveHandler = async () => {
        if (!task?.ID) return;

        if (editingDocid) {
            await db.collection(target.collection).doc(editingDocid).update({
                Title: titleInput,
                Body: noteInput,
            });
        } else {
            const userField = target.collection === 'TaskNotes' ? 'UserID' : 'User';
            const doc = {
                Body: noteInput,
                Title: titleInput,
                ID: uuid(),
                TaskID: task.ID,
                CompagnyID: client,
                Compagny: client,
                Position: dragArray?.length ? dragArray.length + 1 : 1,
                Timestamp: timestamp,
                [userField]: auth.ID || "unknown",
            };

            if (target.parentField) {
                doc[target.parentField] = target.parentValue;
            }

            await db.collection(target.collection).add(doc);
        }

        closeModal(false);
        setSaved('flex');
    };

    // Eerst bevestigen: het prullenbakje verwijderde de notitie voorheen
    // meteen bij de eerste klik.
    const deleteModalHandler = (e) => {
        const docid = e.currentTarget.getAttribute('data-docid');
        const name = e.currentTarget.getAttribute('data-deletename');
        if (!docid) return;

        setDeleteDocid(docid);
        setDeleteName(name || 'deze notitie');
        setDeleteModal(true);
    };

    // Zacht verwijderen (ADR-008), zodat een misklik terug te halen is.
    const deleteNote = async () => {
        await softDeleteDoc({ collection: target.collection, docid: deleteDocid, authId: auth?.ID });
        setSaved('flex');
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(dragArray);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);

        setDragArray(items);

        items.forEach((item, idx) => {
            db.collection(target.collection).doc(item.docid).update({ Position: idx + 1 });
        });

        setSaved('flex');
    };

    const formatDate = (ts) => {
        if (!ts?.toDate) return '';
        return ts.toDate().toLocaleString('nl-NL');
    };

    return (
        <div className="task-notes-container">
            <h3>Notities</h3>

            <ReadOnlyGuard edit="tasks.edit">
            {(canEdit) => (
            <>
            {dragArray && dragArray.length > 0 ? (
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>TITEL</th>
                            <th>DATUM</th>
                            <th>AUTEUR</th>
                            <th>INHOUD</th>
                            <th>VERWIJDEREN</th>
                        </tr>
                    </thead>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="tasknotes" direction="vertical">
                            {(provided) => (
                                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                    {dragArray.map((note, index) => (
                                        <Draggable
                                            key={note.ID}
                                            draggableId={note.ID}
                                            index={index}
                                        >
                                            {(provided) => (
                                                <tr ref={provided.innerRef} {...provided.draggableProps}>
                                                    <td {...provided.dragHandleProps}>
                                                        <DragIndicatorOutlinedIcon />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="task-notes-title-button"
                                                            onClick={() => openEditModal(note)}
                                                        >
                                                            {note.Title || '(geen titel)'}
                                                        </button>
                                                    </td>
                                                    <td>{formatDate(note.Timestamp)}</td>
                                                    <td><UserNameMeta userID={note.UserID || note.User} /></td>
                                                    <td><NoteBodyCell body={note.Body} /></td>
                                                    <td>
                                                        {canEdit && (
                                                            <DeleteOutlinedIcon
                                                                className="table-delete-icon"
                                                                data-docid={note.docid}
                                                                data-deletename={note.Title || 'deze notitie'}
                                                                onClick={deleteModalHandler}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </tbody>
                            )}
                        </Droppable>
                    </DragDropContext>
                </table>
            ) : (
                <p>Geen notities toegevoegd.</p>
            )}

            {canEdit && (
                <AddItemTableRow
                    toolTipContent={'Notitie toevoegen'}
                    onClick={openCreateModal}
                />
            )}

            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteNote}
                deleteName={deleteName}
                softDelete
            />

            <Modal
                modalOpen={showAddModal}
                setModalOpen={closeModal}
                pageHeader={editingDocid ? 'Notitie bewerken' : 'Notitie toevoegen'}
                confirm={saveHandler}
                content={
                    <div className="task-notes-form">
                        <label className="task-notes-form-label">
                            <strong>Titel</strong>
                            <input
                                type="text"
                                value={titleInput}
                                onChange={(e) => setTitleInput(e.target.value)}
                                placeholder="Titel"
                            />
                        </label>
                        <label className="task-notes-form-label task-notes-form-label-grow">
                            <strong>Notitie</strong>
                            <textarea
                                value={noteInput}
                                onChange={(e) => setNoteInput(e.target.value)}
                                placeholder="Voeg hier een notitie toe..."
                            />
                        </label>
                    </div>
                }
            />
            </>
            )}
            </ReadOnlyGuard>
        </div>
    );
};

export default TaskNotes;
